import {
  apiFetch,
  API_BASE_URL,
  buildHeaders,
  setAuthToken,
  CreditError,
  LoginAuthError,
  VerificationExpiredError,
} from './config';
import {
  type Credits,
  type FeatureCost,
  type CreditPack,
  type CreditLedgerResponse,
  type HistoryItem,
  type Job,
  type PremiumJob,
  type HairAnalysisJob,
  type MakeupAnalysisJob,
  type SkinDetail,
  type User,
  type FavoriteListResponse,
  type ProductListResponse,
  type Ingredient,
  type IngredientListResponse,
  type ProductView,
  type ProductSearchResultItem,
  type AppConfig,
} from '../types/api';
import {
  postJobCreated,
  postJobDeleted,
  postJobCorrected,
  postProductClick as telemetryProductClick,
} from './telemetry';

type JobMutationResult = { success: boolean; job_id: string };
interface CreditCostsResponse { costs?: FeatureCost[] }
interface CreditPacksResponse { packs?: CreditPack[] }

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asHistoryItems(value: unknown): HistoryItem[] {
  if (Array.isArray(value)) return value as HistoryItem[];
  if (isRecord(value) && Array.isArray(value.jobs)) return value.jobs as HistoryItem[];
  if (isRecord(value) && Array.isArray(value.items)) return value.items as HistoryItem[];
  return [];
}

function apiErrorMessage(payload: unknown, fallback: string): string {
  if (!isRecord(payload)) return fallback;
  if (typeof payload.error === 'string') return payload.error;
  if (typeof payload.message === 'string') return payload.message;
  return fallback;
}

function trackJobSideEffect(jobId: string | undefined, fn: (id: string) => Promise<void>): void {
  if (jobId) void fn(jobId).catch(() => { /* telemetry non-fatal */ });
}

// ---------- Auth Types ----------
export type LoginOk = { 'jwt-key': string };
export type LoginNotVerified = { email: string; error: string };

export interface Me {
  id: string;
  email: string;
  name?: string;
  surname?: string;
  country?: string;
  city?: string;
  birthdate?: string;
  gender?: string;
  [key: string]: unknown;
}

export interface EmailVerificationOut { verified: boolean }
export interface PwRecoveryOut { verified: boolean }
export interface PwTokenVerifyOut { verified: boolean; email?: string }
export interface PwResetOut { status: boolean }

// -------------------- AUTH --------------------
export async function login(email: string, password: string): Promise<LoginOk> {
  try {
    return await apiFetch<LoginOk>('auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  } catch (error: unknown) {
    if (error instanceof LoginAuthError) {
      throw error;
    }
    throw error;
  }
}

/** Where a user's account deletion stands (`days_left` rounds up). */
export interface AccountDeletionStatus {
  scheduled: boolean;
  requested_at?: string;
  scheduled_for?: string;
  days_left: number;
  /** Length of the redemption period new requests get. */
  grace_days: number;
}

/** The signed-in user's deletion status (`GET /v1/user/account-deletion`). */
export const getAccountDeletionStatus = (): Promise<AccountDeletionStatus> =>
  apiFetch<AccountDeletionStatus>('user/account-deletion');

/**
 * Schedules the signed-in user's account for deletion after the redemption
 * period (`POST /v1/user/account-deletion`). Nothing is erased yet and the
 * request can be cancelled until the date it returns.
 */
export const scheduleAccountDeletion = (): Promise<AccountDeletionStatus> =>
  apiFetch<AccountDeletionStatus>('user/account-deletion', {
    method: 'POST',
    body: JSON.stringify({ source: 'web' }),
  });

/** Withdraws the signed-in user's scheduled deletion. */
export const cancelAccountDeletion = (): Promise<AccountDeletionStatus> =>
  apiFetch<AccountDeletionStatus>('user/account-deletion', { method: 'DELETE' });

/**
 * Asks the backend to email a link that lets the owner of `email` schedule the
 * deletion of an account they cannot sign in to. The answer is identical
 * whether or not an account exists, by design.
 */
export const requestAccountDeletionLink = async (email: string): Promise<void> => {
  await apiFetch('auth/account-deletion/request', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
};

/** True while an emailed deletion link is still valid (does not consume it). */
export async function isAccountDeletionLinkValid(token: string): Promise<boolean> {
  const res = await fetch(
    `${API_BASE_URL}auth/account-deletion/verification?token=${encodeURIComponent(token)}`,
    { headers: buildHeaders() }
  );
  return res.ok;
}

async function postAccountDeletionToken(
  path: string,
  token: string
): Promise<AccountDeletionStatus> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: { ...buildHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (res.ok) return res.json() as Promise<AccountDeletionStatus>;
  if (res.status === 410) throw new VerificationExpiredError();
  throw new Error('Account deletion request failed');
}

/** Schedules the deletion the emailed link belongs to. Throws VerificationExpiredError on a dead link. */
export const confirmAccountDeletion = (token: string): Promise<AccountDeletionStatus> =>
  postAccountDeletionToken('auth/account-deletion/confirm', token);

/** Cancels a scheduled deletion from the emailed cancel link. Throws VerificationExpiredError on a dead link. */
export const cancelAccountDeletionByLink = (token: string): Promise<AccountDeletionStatus> =>
  postAccountDeletionToken('auth/account-deletion/cancel', token);

export async function getMe(): Promise<Me> {
  const res = await apiFetch<{ user?: Me } | Me>('auth/me');
  if (typeof res === 'object' && 'user' in res) return res.user as Me;
  return res as Me;
}

export const register = async (
  body: { email: string; password: string }
): Promise<Me | undefined> => {
  const res = await apiFetch<{ user?: Me; 'jwt-key'?: string }>('auth/register', {
    method: 'POST',
    body: JSON.stringify(body),
  });

  // Check for auto-login token
  if (typeof res === 'object') {
    if ('jwt-key' in res && typeof res['jwt-key'] === 'string') {
      setAuthToken(res['jwt-key']);
    }

    if ('user' in res) {
      return (res).user;
    }
  }

  // Otherwise, nothing is returned
  return undefined;
};

export const setProfile = (body: {
  name?: string;
  surname?: string;
  country?: string;
  city?: string;
  birthdate?: string;
  gender?: string;
}): Promise<User> =>
  apiFetch<User>('auth/set', { method: 'POST', body: JSON.stringify(body) });

export async function verifyEmail(token: string): Promise<EmailVerificationOut> {
  const res = await fetch(
    `${API_BASE_URL}auth/email-verification?token=${encodeURIComponent(token)}`,
    { headers: buildHeaders() }
  );
  if (res.ok) return res.json() as Promise<PwTokenVerifyOut>;
  if (res.status === 410) throw new VerificationExpiredError();
  throw new Error('Verification failed');
}

export const requestPasswordRecovery = async (
  email: string
): Promise<PwRecoveryOut> =>
  apiFetch<PwRecoveryOut>('auth/password-recovery', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });

export async function verifyPasswordToken(
  token: string
): Promise<PwTokenVerifyOut> {
  const res = await fetch(
    `${API_BASE_URL}auth/password-recovery/verification?token=${encodeURIComponent(token)}`,
    { headers: buildHeaders() }
  );
  if (res.ok) return res.json() as Promise<EmailVerificationOut>;
  if (res.status === 410) throw new VerificationExpiredError();
  throw new Error('Verification failed');
}

export async function resetPassword(
  token: string,
  password: string
): Promise<PwResetOut> {
  const res = await fetch(`${API_BASE_URL}auth/password-recovery/reset`, {
    method: 'POST',
    headers: { ...buildHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, password }),
  });
  if (res.ok) return res.json() as Promise<PwResetOut>;
  if (res.status === 410) throw new VerificationExpiredError();
  throw new Error('Reset failed');
}

export const detectFaces = (binary: Blob): Promise<{ response: string }> =>
  apiFetch<{ response: string }>('face', { method: 'POST', body: binary, binary: true });
export const logout = (): Promise<undefined> =>
  apiFetch<undefined>('auth/logout', { method: 'POST' });

export const resendVerificationEmail = async (
  email: string,
): Promise<{ sent: boolean }> => {
  const res = await fetch(`${API_BASE_URL}auth/email-verification/resend`, {
    method: 'POST',
    headers: { ...buildHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  // This endpoint deliberately answers 200 {"sent": true} for every
  // caller-observable outcome — address unknown, already verified, or inside
  // the resend cooldown. It is unauthenticated, so distinguishing those (the
  // 404 / 409 / 429 this used to surface) let anyone feed it an email list and
  // learn who holds an account here and how far through signup they are. On a
  // skin-health product that association is itself sensitive.
  //
  // So there is nothing left to branch on: show the same confirmation whatever
  // happened, and treat only genuine transport/server faults as errors.
  if (res.status === 400) throw new Error('Invalid email payload');
  if (res.status === 422) throw new Error('Invalid request payload');
  if (!res.ok) {
    const payload = await res.json().catch(() => null) as { error?: string } | null;
    throw new Error(payload?.error ?? 'Failed to resend email');
  }
  return res.json() as Promise<{ sent: boolean }>;
};

// -------------------- AVATARS & IMAGES --------------------
export const uploadAvatar = (binary: Blob | ArrayBuffer): Promise<{ success?: boolean }> =>
  apiFetch<{ success?: boolean }>('user/avatar', { method: 'POST', body: binary, binary: true });

export const avatarUrl = (filename: string) => {
  if (!filename) return '';
  // If already a full URL, return as-is
  if (filename.startsWith('http://') || filename.startsWith('https://')) {
    return filename;
  }
  // If already starts with /v1/, remove the /v1/ prefix and prepend base URL
  if (filename.startsWith('/v1/')) {
    const pathWithoutV1 = filename.slice(4); // Remove '/v1/'
    return `${API_BASE_URL}${pathWithoutV1}`;
  }
  // If starts with just /, remove it
  if (filename.startsWith('/')) {
    return `${API_BASE_URL}${filename.slice(1)}`;
  }
  // Otherwise, prepend base URL
  return `${API_BASE_URL}${filename}`;
};
export const imageUrl = (filename: string) => {
  if (!filename) return '';
  // If already a full URL, return as-is
  if (filename.startsWith('http://') || filename.startsWith('https://')) {
    return filename;
  }
  // If already starts with /v1/, remove the /v1/ prefix and prepend base URL
  if (filename.startsWith('/v1/')) {
    const pathWithoutV1 = filename.slice(4); // Remove '/v1/'
    return `${API_BASE_URL}${pathWithoutV1}`;
  }
  // If starts with just /, remove it
  if (filename.startsWith('/')) {
    return `${API_BASE_URL}${filename.slice(1)}`;
  }
  // Otherwise, prepend base URL
  return `${API_BASE_URL}${filename}`;
};

// -------------------- CREDITS / HISTORY / JOBS --------------------
/** Dual-balance credit wallet for the current user. */
export const getCredits = (): Promise<Credits> => apiFetch<Credits>('user/credits');

/**
 * Backwards-compatible alias. `/v1/user/limits` now returns the credit shape,
 * so this resolves to {@link Credits} just like {@link getCredits}.
 */
export const getLimits = (): Promise<Credits> => apiFetch<Credits>('user/limits');

/** Per-feature credit costs (always display costs from the API). */
export const getCreditCosts = async (): Promise<FeatureCost[]> => {
  const res = await apiFetch<CreditCostsResponse>('credits/costs');
  return res.costs ?? [];
};

/** Purchasable surplus-credit packs. */
export const getCreditPacks = async (): Promise<CreditPack[]> => {
  const res = await apiFetch<CreditPacksResponse>('credits/packs');
  return res.packs ?? [];
};

/** Buy a surplus-credit pack. Returns the updated balances. */
export const purchaseCredits = (
  packId: string,
  reference?: string,
): Promise<{ monthly_balance: number; surplus_balance: number; total: number }> =>
  apiFetch<{ monthly_balance: number; surplus_balance: number; total: number }>('billing/purchase-credits', {
    method: 'POST',
    body: JSON.stringify(reference ? { pack_id: packId, reference } : { pack_id: packId }),
  });

/** Paginated credit ledger (wallet history) for the current user. */
export const getCreditLedger = (
  limit = 20,
  offset = 0,
): Promise<CreditLedgerResponse> => {
  const query = new URLSearchParams();
  query.append('limit', String(limit));
  query.append('offset', String(offset));
  return apiFetch<CreditLedgerResponse>(`user/credits/ledger?${query.toString()}`);
};

export const requestDataExport = (userId: string): Promise<{ success?: boolean }> => {
  return apiFetch<{ success?: boolean }>(`users/${userId}/data-export/request`, { method: 'POST' });
};

export const getExportDownload = async (userId: string, exportId: string): Promise<Blob> => {
  const res = await fetch(`${API_BASE_URL}users/${userId}/data-export/download/${exportId}`, {
    method: 'GET',
    headers: buildHeaders(),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ error: 'Download failed' })) as { error?: string };
    throw new Error(errorData.error ?? 'Download failed');
  }
  return res.blob();
};

export const getHistory = async (
  params?: { limit?: number; page?: number; type?: string }
): Promise<{ items: HistoryItem[] }> => {
  const query = new URLSearchParams();
  if (params?.limit) query.append('limit', String(params.limit));
  if (params?.page) query.append('page', String(params.page));
  if (params?.type) query.append('type', params.type);
  const qs = query.toString();
  const path = `user/history${qs ? `?${qs}` : ''}`;
  const res = await apiFetch(path);
  return { items: asHistoryItems(res) };
};

// -------------------- CORRECTED JOBS --------------------
export type CorrectedJobDTO = {
  corrected_job: {
    job_id: string;
    user_id?: string;
    created_at?: string;
    skin_oily: number;
    skin_dry: number;
    skin_normal: number;
    skin_darkcircle: number;
    skin_skinredness: number;
    skin_vascular: number;
    skin_wrinkle: number;
    skin_acne: number;
  };
};

export type PostCorrectJobPayload = {
  job_id: string;
  skin_oily: number;
  skin_dry: number;
  skin_normal: number;
  skin_darkcircle: number;
  skin_skinredness: number;
  skin_vascular: number;
  skin_wrinkle: number;
  skin_acne: number;
};

export async function getCorrectedJob(jobId: string): Promise<CorrectedJobDTO> {
  return apiFetch<CorrectedJobDTO>(`correct_job/${jobId}`);
}

export async function postCorrectJob(body: PostCorrectJobPayload): Promise<JobMutationResult> {
  const result = await apiFetch<JobMutationResult>('correct_job', { method: 'POST', body: JSON.stringify(body) });
  trackJobSideEffect(result.success ? body.job_id : undefined, postJobCorrected);
  return result;
}

export const createJob = async (
  binary: Blob | ArrayBuffer
): Promise<JobMutationResult> => {
  const result = await apiFetch<JobMutationResult>('jobs', { method: 'POST', body: binary, binary: true });
  trackJobSideEffect(result.success ? result.job_id : undefined, postJobCreated);
  return result;
};

export const getJob = (id: string): Promise<Job> =>
  apiFetch<Job>(`jobs/${id}`);

export const createPremiumJob = async (
  binary: Blob | ArrayBuffer
): Promise<JobMutationResult> => {
  const result = await apiFetch<JobMutationResult>('premium-scan', { method: 'POST', body: binary, binary: true });
  trackJobSideEffect(result.success ? result.job_id : undefined, postJobCreated);
  return result;
};

export const getPremiumJob = (id: string): Promise<{ job: PremiumJob }> =>
  apiFetch<{ job: PremiumJob }>(`premium-scan/${id}`);

export const cancelJob = async (id: string): Promise<void> => {
  await apiFetch<undefined>(`jobs/${id}`, { method: 'DELETE' });
  trackJobSideEffect(id, postJobDeleted);
};

export const cancelPremiumJob = async (id: string): Promise<void> => {
  await apiFetch<undefined>(`premium-scan/${id}`, { method: 'DELETE' });
  trackJobSideEffect(id, postJobDeleted);
};

export const createHairAnalysisJob = async (
  binary: Blob | ArrayBuffer
): Promise<JobMutationResult> => {
  return apiFetch<JobMutationResult>('hair-analysis', { method: 'POST', body: binary, binary: true });
};

export const getHairAnalysisJob = (id: string): Promise<{ job: HairAnalysisJob }> =>
  apiFetch<{ job: HairAnalysisJob }>(`hair-analysis/${id}`);

export const cancelHairAnalysisJob = async (id: string): Promise<void> => {
  await apiFetch<undefined>(`hair-analysis/${id}`, { method: 'DELETE' });
};

export const createMakeupAnalysisJob = async (
  binary: Blob | ArrayBuffer
): Promise<JobMutationResult> => {
  return apiFetch<JobMutationResult>('makeup-analysis', { method: 'POST', body: binary, binary: true });
};

export const getMakeupAnalysisJob = (id: string): Promise<{ job: MakeupAnalysisJob }> =>
  apiFetch<{ job: MakeupAnalysisJob }>(`makeup-analysis/${id}`);

export const cancelMakeupAnalysisJob = async (id: string): Promise<void> => {
  await apiFetch<undefined>(`makeup-analysis/${id}`, { method: 'DELETE' });
};

// -------------------- SKIN DETAILS & PRODUCTS --------------------
export const getSkin = (jobId: string): Promise<SkinDetail> =>
  apiFetch<SkinDetail>(`skin/${jobId}`);

export const searchProducts = async (body: { product_keywords: string; limit?: number }) => {
  const res = await apiFetch<{ products?: ProductSearchResultItem[] }>('skin/product', { method: 'POST', body: JSON.stringify(body) });
  return res.products ?? [];
};

// Public per-platform app config (affiliate disclosure flag, etc.).
export const getAppConfig = (): Promise<AppConfig> => apiFetch<AppConfig>('config');
// Product click now uses telemetry client directly with origin auto-added
export const productClick = (body: {
  product_keyword: string;
  product_link: string;
}): Promise<void> => {
  return telemetryProductClick(body.product_keyword, body.product_link);
};

// -------------------- PLANS / BILLING --------------------
export interface BillingInitResponse { url?: string; [key: string]: unknown }
export interface SubscribeResponse { success?: boolean; [key: string]: unknown }

export const initBilling = (): Promise<BillingInitResponse> =>
  apiFetch<BillingInitResponse>('billing/init');

export const subscribe = (body: Record<string, unknown>): Promise<SubscribeResponse> =>
  apiFetch<SubscribeResponse>('billing/subscribe', { method: 'POST', body: JSON.stringify(body) });


// -------------------- QUEUE --------------------
type JobQueueResponseRaw = { job_queue: number };
export type JobQueueResponse = { queue: number | null };

export const getJobQueue = async (id: string): Promise<JobQueueResponse> => {
  const res = await apiFetch<JobQueueResponseRaw>(`jobs/queue/${id}`);
  return { queue: res.job_queue };
};
// --- Product Scanner API ---

export type ScanRequest = { barcode: string };
export type ScanSuccess =
  | { ok: true; product_id: number; source: 'db' | 'ai'; barcode: string; usage_after?: number }
  | { ok: false; barcode: string; message?: string; usage_after?: number }; // 422 cases

export async function postScanBarcode(payload: ScanRequest): Promise<ScanSuccess> {
  const res = await fetch(`${API_BASE_URL}product-scanner`, {
    method: 'POST',
    headers: { ...buildHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (res.status === 401) throw new Error('Unauthorized');
  if (res.status === 402) throw new CreditError(402, await res.json().catch(() => null));
  if (res.status === 403) throw new CreditError(403, await res.json().catch(() => null));
  if (res.status === 422) return res.json() as Promise<ScanSuccess>; // banned/invalid include message
  if (!res.ok) {
    const j: unknown = await res.json().catch(() => ({}));
    throw new Error(apiErrorMessage(j, `Scan failed (${res.status})`));
  }
  return res.json() as Promise<ScanSuccess>;
}

export type ScanImageSuccess =
  | { ok: true; product_id: number; source: 'ai'; barcode: string; usage_after?: number; brand?: string; name?: string; image_url?: string }
  | { ok: false; message?: string; usage_after?: number }; // 422 cases

export async function postScanImage(imageFile: File): Promise<ScanImageSuccess> {
  const formData = new FormData();
  formData.append('image', imageFile);

  const res = await fetch(`${API_BASE_URL}product-scanner/image`, {
    method: 'POST',
    headers: { ...buildHeaders() }, // Don't set Content-Type, let browser set it with boundary
    body: formData,
  });
  if (res.status === 401) throw new Error('Unauthorized');
  if (res.status === 402) throw new CreditError(402, await res.json().catch(() => null));
  if (res.status === 403) throw new CreditError(403, await res.json().catch(() => null));
  if (res.status === 413) throw new Error('Image too large (max 2MB)');
  if (res.status === 422) return res.json() as Promise<ScanImageSuccess>; // not skincare/cosmetic or extraction failed
  if (res.status === 502) throw new Error('AI service temporarily unavailable');
  if (!res.ok) {
    const j: unknown = await res.json().catch(() => ({}));
    throw new Error(apiErrorMessage(j, `Image scan failed (${res.status})`));
  }
  return res.json() as Promise<ScanImageSuccess>;
}

export type ResultSuccess = {
  ok: true;
  product_id: number;
  barcode: string;
  analysis: {
    score?: number;
    score_raw?: number;
    ingredients?: { id?: number; inci_name?: string; [k: string]: unknown }[];
    // INCI-enriched, adaptive signals — present only when INCI data exists.
    skin_fit?: 'suitable' | 'neutral' | 'avoid';
    highlights?: { target: string; evidence_level?: string; ingredients?: string[] }[];
    product_quality?: {
      inci_quality_score?: number;
      clean_beauty_score?: number;
      certifications?: string[];
      spf?: number;
    };
    analysis_quality?: 'inci_verified' | 'partial' | 'estimated';
    coverage?: number;
    [k: string]: unknown;
  };
  is_favorite?: boolean;
  image_url?: string;
};

export async function getProductAnalysis(productId: number, barcode: string): Promise<ResultSuccess> {
  // NOTE: GET with a body works in fetch, but some proxies drop it. If you see 400,
  // consider switching the backend to POST /product-scanner/{id}.
  const res = await fetch(`${API_BASE_URL}product-scanner/${productId}`, {
    method: 'POST',
    headers: { ...buildHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ barcode }),
  });
  if (res.status === 401) throw new Error('Unauthorized');
  if (res.status === 402) throw new CreditError(402, await res.json().catch(() => null));
  if (res.status === 403) throw new Error('Barcode mismatch');
  if (res.status === 404) throw new Error('Product not found');
  if (!res.ok) {
    const j: unknown = await res.json().catch(() => ({}));
    throw new Error(apiErrorMessage(j, `Analysis failed (${res.status})`));
  }
  return res.json() as Promise<ResultSuccess>;
}

// ----- Favorites -----

export async function listFavoriteProducts(
  params: { limit?: number; offset?: number } = {},
): Promise<FavoriteListResponse> {
  const query = new URLSearchParams();
  if (typeof params.limit === 'number') query.append('limit', String(params.limit));
  if (typeof params.offset === 'number') query.append('offset', String(params.offset));
  const suffix = query.toString();
  const path = `favorites${suffix ? `?${suffix}` : ''}`;
  return apiFetch<FavoriteListResponse>(path);
}

export const addFavoriteProduct = (productId: number): Promise<{ ok: boolean; favorite_id: number; product_id: number; favorited_at: string }> =>
  apiFetch<{ ok: boolean; favorite_id: number; product_id: number; favorited_at: string }>('favorites', { method: 'POST', body: JSON.stringify({ product_id: productId }) });

export const removeFavoriteProduct = (productId: number): Promise<{ ok: boolean }> =>
  apiFetch<{ ok: boolean }>(`favorites/${productId}`, { method: 'DELETE' });

export const getFavoriteStatus = (productId: number): Promise<{ ok: boolean; favorited: boolean }> =>
  apiFetch<{ ok: boolean; favorited: boolean }>(`favorites/${productId}/status`);

export async function listProducts(
  params: { limit?: number; offset?: number; q?: string } = {},
): Promise<ProductListResponse> {
  const query = new URLSearchParams();
  if (typeof params.limit === 'number') query.append('limit', String(params.limit));
  if (typeof params.offset === 'number') query.append('offset', String(params.offset));
  if (params.q) query.append('q', params.q);
  const suffix = query.toString();
  const path = `products${suffix ? `?${suffix}` : ''}`;
  return apiFetch<ProductListResponse>(path);
}

export async function listIngredients(
  params: { limit?: number; offset?: number; name?: string; functions?: string[]; allergen_flags?: string[]; vegan?: boolean; halal?: boolean } = {},
): Promise<IngredientListResponse> {
  const query = new URLSearchParams();
  if (typeof params.limit === 'number') query.append('limit', String(params.limit));
  if (typeof params.offset === 'number') query.append('offset', String(params.offset));
  if (params.name) query.append('name', params.name);
  if (params.functions && params.functions.length > 0) query.append('functions', params.functions.join(','));
  if (params.allergen_flags && params.allergen_flags.length > 0) query.append('allergen_flags', params.allergen_flags.join(','));
  if (typeof params.vegan === 'boolean') query.append('vegan', String(params.vegan));
  if (typeof params.halal === 'boolean') query.append('halal', String(params.halal));
  const suffix = query.toString();
  const path = `ingredients${suffix ? `?${suffix}` : ''}`;
  return apiFetch<IngredientListResponse>(path);
}

export const getProductView = (productId: number): Promise<ProductView> =>
  apiFetch<ProductView>(`products/${productId}`);

export const getIngredient = (id: number): Promise<Ingredient> =>
  apiFetch<Ingredient>(`ingredients/${id}`);

// ----- Product comparison & compatibility -----

export type ProductComparePayload = {
  barcode_a: string;
  barcode_b: string;
  /** Identify a side by product id — required for slug-only products without a barcode. */
  product_id_a?: number;
  product_id_b?: number;
};

export type ProductCompareProduct = {
  product_id: number;
  barcode: string;
  analysis: Record<string, unknown>;
  image_url?: string;
};

export type ProductCompareSummary = {
  better_overall: 'a' | 'b' | 'tie';
  better_for_user: 'a' | 'b' | 'tie';
  delta_overall: number;
  delta_for_user: number;
};

export type ProductCompareResponse = {
  ok: boolean;
  barcode_a: string;
  barcode_b: string;
  usage_after?: number;
  /** false when this pair was already in history and re-run for free (no credits). */
  charged?: boolean;
  product_a: ProductCompareProduct;
  product_b: ProductCompareProduct;
  comparison: ProductCompareSummary;
};

export const postProductCompare = async (
  payload: ProductComparePayload,
): Promise<ProductCompareResponse> => {
  const res = await fetch(`${API_BASE_URL}product-scanner/compare`, {
    method: 'POST',
    headers: { ...buildHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (res.status === 401) throw new Error('Unauthorized');
  if (res.status === 402) throw new CreditError(402, await res.json().catch(() => null));
  if (res.status === 403) throw new CreditError(403, await res.json().catch(() => null));
  if (res.status === 422) {
    const data: unknown = await res.json().catch(() => null);
    throw new Error(apiErrorMessage(data, 'Invalid barcode'));
  }
  if (!res.ok) {
    const data: unknown = await res.json().catch(() => null);
    throw new Error(apiErrorMessage(data, `Comparison failed (${res.status})`));
  }
  return res.json() as Promise<ProductCompareResponse>;
};

export type ProductCompatPayload = {
  barcode_a: string;
  barcode_b: string;
  /** Identify a side by product id — required for slug-only products without a barcode. */
  product_id_a?: number;
  product_id_b?: number;
  context?: string;
  order?: string;
};

export type ProductCompatRuleHit = {
  rule_id: string;
  name: string;
  kind: string;
  base_delta: number;
  weight: number;
  final_delta: number;
  avg_evidence: number;
  note?: string;
  summary: string;
  details?: Record<string, unknown>;
  evidence_pairs?: {
    left: Record<string, unknown>;
    right: Record<string, unknown>;
    score: number;
  }[];
};

export type ProductCompatAdjustment = {
  code: string;
  description: string;
  delta: number;
  weight: number;
};

export type ProductCompatComparison = {
  total_score: number;
  normalized_score: number;
  abstain: boolean;
  rule_hits?: ProductCompatRuleHit[];
  preference_adjustments?: ProductCompatAdjustment[];
  explanations?: string[];
  warnings?: string[];
  unmatched_ingredients?: Record<string, string[]>;
};

export type ProductCompatResponse = {
  ok: boolean;
  barcode_a: string;
  barcode_b: string;
  context: string;
  order: string;
  message?: string;
  usage_after?: number;
  /** false when this pair was already in history and re-run for free (no credits). */
  charged?: boolean;
  comparison?: ProductCompatComparison;
  image_url_a?: string | null;
  image_url_b?: string | null;
};

export const postProductCompatibility = async (
  payload: ProductCompatPayload,
): Promise<ProductCompatResponse> => {
  const res = await fetch(`${API_BASE_URL}product-compat/compare`, {
    method: 'POST',
    headers: { ...buildHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (res.status === 401) throw new Error('Unauthorized');
  if (res.status === 402) throw new CreditError(402, await res.json().catch(() => null));
  if (res.status === 403) throw new CreditError(403, await res.json().catch(() => null));
  if (!res.ok) {
    const data: unknown = await res.json().catch(() => null);
    throw new Error(apiErrorMessage(data, `Compatibility failed (${res.status})`));
  }
  return res.json() as Promise<ProductCompatResponse>;
};

export type Prefs = {
  allergies: string[];
  sensitivity: 'none' | 'possible' | 'likely';
  acne_prone: boolean;
  ethical_mode: 'off' | 'vegan' | 'halal' | 'cruelty_free';
  cautious_actives: boolean;
  skin_type?: 'oily' | 'dry' | 'normal' | 'combination' | null;
};

export type PrefsOut = Prefs & { user_id: string; created_at: string; updated_at: string };

export async function getScannerPrefs(): Promise<PrefsOut> {
  const res = await fetch(`${API_BASE_URL}product-scanner/preferences`, {
    headers: { ...buildHeaders() },
  });
  if (res.status === 401) throw new Error('Unauthorized');
  if (!res.ok) throw new Error('Failed to load preferences');
  return res.json() as Promise<PrefsOut>;
}

export async function upsertScannerPrefs(p: Partial<Prefs>): Promise<PrefsOut> {
  const res = await fetch(`${API_BASE_URL}product-scanner/preferences`, {
    method: 'POST',
    headers: { ...buildHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(p),
  });
  if (res.status === 401) throw new Error('Unauthorized');
  if (res.status === 422) throw new Error('Invalid enum value');
  if (!res.ok) {
    const j: unknown = await res.json().catch(() => ({}));
    throw new Error(apiErrorMessage(j, 'Failed to save preferences'));
  }
  return res.json() as Promise<PrefsOut>;
}

// ----- Weather Analyzer -----
export type WeatherFactorKey = 'uv' | 'pollution' | 'hydration' | 'temperature' | 'wind';
export type WeatherFactorLevel = 'none' | 'low' | 'moderate' | 'high' | 'severe';

export type WeatherFactor = {
  key: WeatherFactorKey;
  label: string;
  icon: string;
  value: number;
  display_value: string;
  unit: string;
  stress: number;              // 0-100, HIGH = worse
  level: WeatherFactorLevel;
  weight: number;              // 0-1 share of today's deduction
  headline: string;
  detail: string;
  personalized_note?: string;
};

export type WeatherRecommendation = {
  priority: 'high' | 'medium' | 'low';
  tag: string;
  icon: string;
  title: string;
  body: string;
};

export type WeatherGrade = 'excellent' | 'good' | 'fair' | 'poor' | 'harsh';

export type SkinAnalysis = {
  score: number;               // 0-100, HIGH = GOOD (skin-friendly)
  grade: WeatherGrade;
  headline: string;
  summary: string;
  personalized: boolean;
  profile_factors: string[];
  factors: WeatherFactor[];
  recommendations: WeatherRecommendation[];
};

export type WeatherAnalyzeResponse = {
  ok: boolean;
  weather: {
    location_name: string;
    lat: number;
    lon: number;
    localtime_epoch: number;
    uv: number;
    humidity: number;
    dewpoint_c: number;
    temp_c: number;
    wind_kph: number;
    gust_kph: number;
    cloud: number;
    precip_mm: number;
    is_day: 0 | 1;
    air_quality?: {
      co: number;
      no2: number;
      o3: number;
      so2: number;
      pm2_5: number;
      pm10: number;
      'us-epa-index': number;
      'gb-defra-index': number;
    };
  };
  analysis: SkinAnalysis;
};

export async function getWeatherAnalyze(lat: number, lon: number, aqi = true): Promise<WeatherAnalyzeResponse> {
  const qs = new URLSearchParams({
    lat: String(lat),
    lon: String(lon),
    aqi: aqi ? 'true' : 'false',
  }).toString();

  // apiFetch auto-adds headers (incl. Authorization)
  const res = await apiFetch<WeatherAnalyzeResponse>(`weather/analyze?${qs}`);
  return res;
}

// ====== Compatibility Preferences (independent TS types) ======
export type CompatSensitivity = 'none' | 'possible' | 'likely';
export type CompatEthicalMode = 'off' | 'vegan' | 'halal' | 'cruelty_free';

// Optional: keep a central list to drive UI checkboxes
export const COMPAT_ALLERGENS = [
  'nickel',
  'benzoic_acid',
  'methylisothiazolinone',
  'methylchloroisothiazolinone',
  'parabens',
  'lanolin',
  'fragrance',
  'linalool',
  'limonene',
  'citral',
  'hydroxycitronellal',
  'balsam_peru',
  'tocopherol_allergy',
] as const;
export type CompatAllergen = typeof COMPAT_ALLERGENS[number];

export type CompatPrefs = {
  allergies: string[]; // keep as string[] to match backend payload
  sensitivity: CompatSensitivity;
  acne_prone: boolean;
  ethical_mode: CompatEthicalMode;
  cautious_actives: boolean;
};

export type CompatPrefsOut = CompatPrefs & {
  user_id: string;
  created_at: string;
  updated_at: string;
};

/** Load user prefs for the compatibility engine */
export async function getCompatPrefs(): Promise<CompatPrefsOut> {
  const res = await fetch(`${API_BASE_URL}product-compat/preferences`, {
    headers: { ...buildHeaders() },
  });
  if (res.status === 401) throw new Error('Unauthorized');
  if (!res.ok) throw new Error('Failed to load compatibility preferences');
  return res.json() as Promise<CompatPrefsOut>;
}

/** Upsert (partial) user prefs for the compatibility engine */
export async function upsertCompatPrefs(p: Partial<CompatPrefs>): Promise<CompatPrefsOut> {
  const res = await fetch(`${API_BASE_URL}product-compat/preferences`, {
    method: 'POST',
    headers: { ...buildHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(p),
  });
  if (res.status === 401) throw new Error('Unauthorized');
  if (res.status === 422) throw new Error('Invalid enum value');
  if (!res.ok) {
    const j: unknown = await res.json().catch(() => ({}));
    throw new Error(apiErrorMessage(j, 'Failed to save compatibility preferences'));
  }
  return res.json() as Promise<CompatPrefsOut>;
}

export async function updateUserPreferences(prefs: {
  photo_ai_consent?: boolean;
  newsletter_opt_in?: boolean;
}): Promise<{ photo_ai_consent: boolean; newsletter_opt_in: boolean }> {
  return apiFetch('user/preferences', {
    method: 'POST',
    body: JSON.stringify(prefs),
  });
}